(ns hai.tasks.card
  "Shared task row for the Tasks mini-app cards: the always-visible part
   (stamp, title, deadline, links) resting on a card that raises and presses to
   reveal whatever the row holds back — see [[hidden-parts]] — plus inline edit
   and delete-confirm. Required by the desktop and mobile Tasks cards and the
   extension sidebar so the row is one implementation.

   Surface differences are passed in: `:hover-actions?` hides edit/delete
   until hover (desktop pointer) versus keeping them shown (touch); `:show-hint?`
   toggles the keyboard submit hint on the edit form (off for touch);
   `:on-open-entity`, if supplied, routes a linked chip's click (absent, the
   chips are display-only); `:change-fx` rides every row mutation's success;
   `:render-extra-actions`, if supplied, is called with the task and rendered
   first in the actions cluster."
  (:require
   [garden.stylesheet]
   [hai.entity.sections :as sections]
   [hai.tags.combobox :as tag-combobox]
   [hai.tasks.assignee-picker :as assignee-picker]
   [hai.tasks.data :as data]
   [hai.tasks.deadline :as deadline]
   [hai.tasks.form :as form]
   [hai.tasks.ui :as ui]
   [hai.tasks.view :as view]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private trash-icon (icons/mui-icon :outlined :delete))
(def ^:private edit-icon (icons/mui-icon :outlined :edit))

(defn- link-count [task]
  (+ (count (:task/activities task))
     (count (:task/contacts task))
     (count (:task/notes task))
     (count (:task/tasks task))))

(defn- hidden-parts
  "What pressing a task reveals — and so what makes its card pressable at all:
   its description, and any row standing behind a \"+N\" pill, which shows none
   of the chips it stands in for. A done task keeps its description filed away;
   a collapsed row still opens it, or it would be unreadable."
  [task]
  (cond-> #{}
    (and (nil? (:task/completed-at task))
         (seq (:task/description task)))              (conj :description)
    (sections/overflows? (link-count task))           (conj :links)
    (sections/overflows? (count (:task/tags task)))   (conj :tags)))

;; The always-visible part of a task: checkbox, title, deadline, and any links.
;; A description is not here — it lives in [[task-detail]], revealed on press.
(o/defstyled task-row :div
  {:padding "14px 18px"
   :display "flex" :flex-direction "column" :gap "10px"
   :position "relative"}
  [:.task-row-main
   {:display "flex" :flex-direction "row"
    :gap "14px" :align-items "center"}]
  ;; Links indent to line up under the title, past the checkbox.
  [:.task-row-links
   {:margin-left "48px"}]
  [:.task-title-block
   {:flex "1" :min-width "0"
    :display "flex" :flex-direction "column" :gap "2px"}]
  [:.task-title
   {:font-size "14px" :font-weight "500" :line-height "1.35"
    :color t/--card-forest
    :word-break "break-word"
    :overflow-wrap "anywhere"}]
  [:.task-title.done {:opacity "0.5" :text-decoration "line-through"}]
  [:.actions
   {:display "flex" :align-items "center" :gap "2px"}]
  ;; Desktop reveals edit/delete only on hover; touch surfaces keep them shown.
  [:&.hover-actions
   [:.actions {:opacity "0"}]
   [:&:hover [:.actions {:opacity "1"}]]]
  [:.icon-btn
   {:width "26px" :height "26px" :padding "0"
    :border "0" :background "transparent"
    :border-radius "6px"
    :color t/--link-muted
    :cursor "pointer"
    :display "inline-flex" :align-items "center" :justify-content "center"}]
  [:.edit-btn
   [:&:hover {:background "rgba(45,62,42,0.10)" :color t/--card-forest}]]
  [:.delete-btn
   [:&:hover {:background "rgba(177,58,48,0.10)" :color t/--property-13}]])

;; A task's description, flush beneath the row inside the pressed card, rising in
;; as one continuous sheet. Its left inset lines the text up under the title.
(o/defstyled task-detail :div
  {:padding "8px 18px 16px 48px"
   :border-top "1px solid" :border-top-color t/--card-forest-10
   :animation "task-detail-in 0.22s ease"}
  [:.task-desc
   {:font-size "13px" :line-height "1.55"
    :color t/--card-forest
    :white-space "pre-wrap"
    :opacity "0.8"}]
  [:&.done [:.task-desc {:opacity "0.55"}]])

;; The surface a task sits on. A task holding something back rests as a raised,
;; pressable card and presses down when opened; one with nothing to reveal stays
;; flat. Elevation — not a badge or chevron — is the signal that a task expands.
(o/defstyled task-shell :div
  {:border-radius "12px"
   :transition "transform 0.2s ease, box-shadow 0.2s ease, background 0.16s ease"}
  [:&.flat
   {:background t/--task-card-surface-flat
    :border-radius "13px"
    :transition "box-shadow 160ms ease"}]
  [:&.raised
   {:background t/--task-card-surface
    :border-radius "16px"
    :box-shadow t/--task-card-shadow
    :cursor "pointer"}]
  ;; Pressed down: shallow shadow, and the detail extrudes inside the rounded box.
  [:&.raised.open
   {:box-shadow t/--task-card-shadow-pressed
    :overflow "hidden"}]
  ;; Hover cues only where hover exists — a tap's sticky :hover would leave the
  ;; card floating until the next tap elsewhere.
  [:at-media {:hover "hover"}
   ;; Only a collapsed card lifts; the lift is the "press me" cue.
   ["&.raised:not(.open):hover"
    {:transform "translateY(-7px)"
     :box-shadow t/--task-card-shadow-hover}]
   ;; A flat row has nothing to open, so it only draws its edge.
   [:&.flat:hover
    {:box-shadow t/--task-card-ring-flat-hover}]]
  ;; A just-added task holds a sage wash then fades — long enough to catch the
  ;; eye. A relative-positioned overlay keeps it off the card's own background
  ;; and drop shadow, so it works the same on flat rows and raised cards.
  [:&.just-added
   {:position "relative"}
   [:&::after
    {:content "''"
     :position "absolute" :inset "0"
     :border-radius "inherit"
     :pointer-events "none"
     :animation "task-just-added 2s ease-out forwards"}]])

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules just-added-keyframes
  (garden.stylesheet/at-keyframes
   :task-just-added
   ["0%"   {:background "rgba(45,62,42,0.16)"}]
   ["55%"  {:background "rgba(45,62,42,0.16)"}]
   ["100%" {:background "rgba(45,62,42,0)"}]))

(o/defstyled task-group :div
  {:transition "background 0.15s ease"}
  [:&.confirming
   {:background "rgba(177,58,48,0.05)"}])

(o/defstyled delete-confirm :div
  {:display "flex" :align-items "center" :justify-content "flex-end"
   :gap "8px"
   :padding "10px 22px 12px"
   :font-size "13px" :color t/--card-forest
   :border-bottom-width "1px" :border-bottom-style "solid" :border-bottom-color t/--property-3}
  [:.prompt {:margin-right "auto" :color t/--link-muted}]
  [:.delete-yes
   {:padding "6px 14px"
    :border "none" :border-radius "8px"
    :background t/--property-13 :color "white"
    :font-family "inherit"
    :font-size "13px" :font-weight "600"
    :cursor "pointer"
    :transition "background 0.15s ease"}
   [:&:hover {:background t/--property-14}]]
  [:.delete-no
   {:padding "6px 10px"
    :background "transparent" :border "none"
    :font-family "inherit"
    :font-size "13px" :color t/--link-muted
    :cursor "pointer"}
   [:&:hover {:color t/--card-forest}]])

(defn task-row-component
  "One task row for the mini-app cards; the surface-difference opts are
   described in the namespace docstring."
  [task {:keys [hover-actions? show-hint? on-open-entity change-fx
                render-extra-actions]}]
  (r/with-let [!expanded? (r/atom false)
               !confirming? (r/atom false)
               !editing? (r/atom false)]
    (let [task-uuid (:task/uuid task)
          title (:task/title task)
          description (:task/description task)
          links (:task/activities task)
          contacts (:task/contacts task)
          linked-notes (:task/notes task)
          linked-tasks (:task/tasks task)
          tags (:task/tags task)
          done? (some? (:task/completed-at task))
          has-extras? (boolean (or (seq tags)
                                   (seq links)
                                   (seq contacts)
                                   (seq linked-notes)
                                   (seq linked-tasks)))
          ;; Tags and the deadline ride on the resting row, so they never make a
          ;; card; everything the row holds back does.
          hidden (hidden-parts task)
          expandable? (boolean (seq hidden))
          open? (and expandable? @!expanded?)
          confirming? @!confirming?
          editing? @!editing?
          highlighted? @(<s [::data/highlighted? task-uuid])
          link-urls (vec (keep :activity/url links))
          start-edit (fn [] (reset! !editing? true))
          cancel-edit (fn [] (reset! !editing? false))
          ;; Home reloads its slice after the save, the way the board re-resolves
          ;; its rows: a tag typed here is patched in without a uuid, and only
          ;; server truth can give the next save something to diff against.
          save-edit (fn [payload]
                      (>e [::data/save-edit task payload
                           (into [[:dispatch [::data/load-tasks-assigned-to-me]]]
                                 change-fx)])
                      (reset! !editing? false))
          toggle-done (fn [e]
                        (.stopPropagation e)
                        (>e [(if done? ::data/uncomplete ::data/complete) task-uuid
                             change-fx]))]
      [task-group {:class (when confirming? "confirming")}
       (if editing?
         [form/editor-frame
          [form/task-form
           {:initial (cond-> {:title title
                              :urgency (get task :task/urgency :task.urgency/later)
                              :urls link-urls
                              :contacts contacts
                              :notes linked-notes
                              :tasks linked-tasks
                              :tags (:task/tags task)
                              :assignee (:task/assigned-to task)}
                       (seq description) (assoc :description description)
                       (:task/deadline-at task) (assoc :deadline (deadline/from-task task)))
            :start-expanded? true
            :show-hint? show-hint?
            :on-save save-edit
            :on-cancel cancel-edit
            :on-close cancel-edit
            :render-assignee-picker
            (fn [{:keys [on-pick trigger]}]
              [assignee-picker/component
               {:editable? true
                :on-pick on-pick
                :label "Assign"
                :trigger trigger}])}]]
         [task-shell
          {:class (cond-> []
                    expandable? (conj "raised")
                    (not expandable?) (conj "flat")
                    open? (conj "open")
                    highlighted? (conj "just-added"))}
          [task-row
           {:class (cond-> []
                     done? (conj "done")
                     hover-actions? (conj "hover-actions"))
            :on-click (fn []
                        (when expandable?
                          (swap! !expanded? not)))}
           [:div.task-row-main
            [ui/stampbox {:done?      done?
                          :task-title title
                          ;; Re-completing re-stamps: a new completed-at remounts
                          ;; the stamp so its jitter is rolled again.
                          :stamp-key  (:task/completed-at task)
                          :on-toggle  toggle-done}]
            [:div.task-title-block
             [:div.task-title
              {:class (when done? "done")}
              title]
             (when-not done?
               [view/task-deadline {:task task}])]
            [:div.actions {:on-click #(.stopPropagation %)}
             (when render-extra-actions
               (render-extra-actions task))
             [:button.icon-btn.edit-btn
              {:type "button"
               :aria-label "Edit"
               :title "Edit"
               :on-click (fn [e]
                           (.stopPropagation e)
                           (start-edit))}
              [edit-icon]]
             [:button.icon-btn.delete-btn
              {:type "button"
               :aria-label "Delete"
               :title "Delete"
               :on-click (fn [e]
                           (.stopPropagation e)
                           (reset! !confirming? true))}
              [trash-icon]]]]
           ;; Every extra a task carries, rendered exactly as a note card
           ;; renders its own: tags, urls and the linked contacts / notes /
           ;; tasks as chips, collapsing past a small count. Read-only here —
           ;; the chips display and route on click; editing is the composer's.
           ;; Pressing the card is what opens a collapsed row, so every chip is
           ;; one press from being read.
           (when has-extras?
             [:div.task-row-links
              [sections/entity-extras
               {:entity-uuid       task-uuid
                :editing?          false
                :expanded?         open?
                :tags              tags
                :link-urls         link-urls
                :contacts          contacts
                :linked-notes      linked-notes
                :linked-tasks      linked-tasks
                :render-tag-search tag-combobox/search-slot
                :on-open-entity    on-open-entity}]])]
          (when (and open? (contains? hidden :description))
            [task-detail {:class (when done? "done")}
             [:div.task-desc description]])])
       (when confirming?
         [delete-confirm
          [:span.prompt "Delete this task?"]
          [:button.delete-no
           {:type "button"
            :on-click (fn [e]
                        (.stopPropagation e)
                        (reset! !confirming? false))}
           "Cancel"]
          [:button.delete-yes
           {:type "button"
            :on-click (fn [e]
                        (.stopPropagation e)
                        (reset! !confirming? false)
                        (>e [::data/delete task-uuid change-fx]))}
           "Delete"]])])))
